import { useState } from 'react'
import { useDocumentTitle } from '@/shared/hooks/use-document-title'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from '@dnd-kit/core'
import type { DragEndEvent, DragStartEvent } from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Settings2, Plus, Pencil, Trash2, MoreHorizontal, Globe, Briefcase, Layers,
  Search, ShieldCheck, ChevronRight, ChevronDown, GripVertical,
} from 'lucide-react'
import {
  usePaises,
  useCargos,
  useLineas,
  useCrearPais,
  useActualizarPais,
  useEliminarPais,
  useCrearCargo,
  useActualizarCargo,
  useEliminarCargo,
  useCrearLinea,
  useActualizarLinea,
  useEliminarLinea,
} from '../hooks/useBdClientes'
import type {
  PaisOption,
  CargoOption,
  LineaOption,
} from '../types'

// ============================================================================
// Modal genérico para crear/editar entidades
// ============================================================================

interface PaisFormData {
  nombre: string
  codigo: string
  activo: boolean
}

interface CargoLineaFormData {
  nombre: string
  descripcion: string
  activo: boolean
}

function PaisModal({
  open,
  onOpenChange,
  editItem,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  editItem: PaisOption | null
}) {
  const [form, setForm] = useState<PaisFormData>({
    nombre: editItem?.nombre || '',
    codigo: editItem?.codigo || '',
    activo: editItem?.activo ?? true,
  })

  const crearMutation = useCrearPais()
  const actualizarMutation = useActualizarPais()
  const isEditing = !!editItem
  const isPending = crearMutation.isPending || actualizarMutation.isPending

  const handleSubmit = async () => {
    if (!form.nombre.trim()) return
    try {
      if (isEditing && editItem) {
        await actualizarMutation.mutateAsync({
          id: editItem.id,
          payload: {
            nombre: form.nombre.trim(),
            codigo: form.codigo.trim() || null,
            activo: form.activo,
          },
        })
      } else {
        await crearMutation.mutateAsync({
          nombre: form.nombre.trim(),
          codigo: form.codigo.trim() || null,
          activo: form.activo,
        })
      }
      onOpenChange(false)
    } catch { /* handled by hook */ }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Editar País' : 'Nuevo País'}</DialogTitle>
          <DialogDescription>
            {isEditing ? 'Modifica los datos del país' : 'Ingresa los datos del nuevo país'}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="pais-nombre">Nombre *</Label>
            <Input
              id="pais-nombre"
              value={form.nombre}
              onChange={(e) => setForm(prev => ({ ...prev, nombre: e.target.value }))}
              placeholder="Ej: Ecuador"
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="pais-codigo">Código ISO</Label>
            <Input
              id="pais-codigo"
              value={form.codigo}
              onChange={(e) => setForm(prev => ({ ...prev, codigo: e.target.value }))}
              placeholder="Ej: EC"
              maxLength={10}
            />
          </div>
          <div className="flex items-center justify-between">
            <Label htmlFor="pais-activo">Activo</Label>
            <Switch
              id="pais-activo"
              checked={form.activo}
              onCheckedChange={(checked) => setForm(prev => ({ ...prev, activo: checked }))}
              className="data-[state=checked]:bg-teal-500"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!form.nombre.trim() || isPending}
            className="bg-teal-800 hover:bg-teal-900 text-white"
          >
            {isPending ? 'Guardando...' : isEditing ? 'Actualizar' : 'Crear'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function CargoLineaModal({
  open,
  onOpenChange,
  editItem,
  entityType,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  editItem: CargoOption | LineaOption | null
  entityType: 'cargo' | 'linea'
}) {
  const [form, setForm] = useState<CargoLineaFormData>({
    nombre: editItem?.nombre || '',
    descripcion: editItem?.descripcion || '',
    activo: editItem?.activo ?? true,
  })

  const crearCargoMutation = useCrearCargo()
  const actualizarCargoMutation = useActualizarCargo()
  const crearLineaMutation = useCrearLinea()
  const actualizarLineaMutation = useActualizarLinea()

  const isEditing = !!editItem
  const isPending = entityType === 'cargo'
    ? crearCargoMutation.isPending || actualizarCargoMutation.isPending
    : crearLineaMutation.isPending || actualizarLineaMutation.isPending

  const label = entityType === 'cargo' ? 'Cargo' : 'Línea'

  const handleSubmit = async () => {
    if (!form.nombre.trim()) return
    const payload = {
      nombre: form.nombre.trim(),
      descripcion: form.descripcion.trim() || null,
      activo: form.activo,
    }
    try {
      if (entityType === 'cargo') {
        if (isEditing && editItem) {
          await actualizarCargoMutation.mutateAsync({ id: editItem.id, payload })
        } else {
          await crearCargoMutation.mutateAsync(payload)
        }
      } else {
        if (isEditing && editItem) {
          await actualizarLineaMutation.mutateAsync({ id: editItem.id, payload })
        } else {
          await crearLineaMutation.mutateAsync(payload)
        }
      }
      onOpenChange(false)
    } catch { /* handled by hook */ }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{isEditing ? `Editar ${label}` : `Nuevo ${label}`}</DialogTitle>
          <DialogDescription>
            {isEditing ? `Modifica los datos del ${label.toLowerCase()}` : `Ingresa los datos del nuevo ${label.toLowerCase()}`}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="entity-nombre">Nombre *</Label>
            <Input
              id="entity-nombre"
              value={form.nombre}
              onChange={(e) => setForm(prev => ({ ...prev, nombre: e.target.value }))}
              placeholder={entityType === 'cargo' ? 'Ej: Director Médico' : 'Ej: Oncología'}
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="entity-descripcion">Descripción</Label>
            <Textarea
              id="entity-descripcion"
              value={form.descripcion}
              onChange={(e) => setForm(prev => ({ ...prev, descripcion: e.target.value }))}
              placeholder="Descripción opcional..."
              rows={3}
            />
          </div>
          <div className="flex items-center justify-between">
            <Label htmlFor="entity-activo">Activo</Label>
            <Switch
              id="entity-activo"
              checked={form.activo}
              onCheckedChange={(checked) => setForm(prev => ({ ...prev, activo: checked }))}
              className="data-[state=checked]:bg-teal-500"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!form.nombre.trim() || isPending}
            className="bg-teal-800 hover:bg-teal-900 text-white"
          >
            {isPending ? 'Guardando...' : isEditing ? 'Actualizar' : 'Crear'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ============================================================================
// Sección genérica de lista con búsqueda
// ============================================================================

interface ConfigSectionProps<T> {
  sectionId: string
  title: string
  icon: React.ElementType
  items: T[] | undefined
  isLoading: boolean
  onAdd: () => void
  onEdit: (item: T) => void
  onDelete: (item: T) => void
  renderSecondary?: (item: T) => string | null
}

function ConfigSection<T extends { id: number; nombre: string; activo: boolean }>({
  sectionId,
  title,
  icon: Icon,
  items,
  isLoading,
  onAdd,
  onEdit,
  onDelete,
  renderSecondary,
}: ConfigSectionProps<T>) {
  const [search, setSearch] = useState('')
  const [open, setOpen] = useState(false)

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: sectionId })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  const filtered = items?.filter(item =>
    item.nombre.toLowerCase().includes(search.toLowerCase())
  )

  return (
    <Card
      ref={setNodeRef}
      style={style}
      className={`border border-gray-300 dark:border-gray-700 transition-colors ${isDragging ? 'opacity-40 border-dashed border-2 border-teal-400 bg-teal-50/30 dark:bg-teal-950/30' : ''}`}
    >
      <CardHeader className="py-3 px-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <button
              {...attributes}
              {...listeners}
              className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground"
            >
              <GripVertical className="h-4 w-4" />
            </button>
            <button
              onClick={() => setOpen(!open)}
              className="flex items-center gap-2 hover:text-foreground transition-colors"
            >
              {open ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
              <Icon className="h-4 w-4 text-teal-500" />
              <CardTitle className="text-base font-semibold">
                {title}
              </CardTitle>
              <span className="text-sm text-muted-foreground">
                ({items?.length ?? 0})
              </span>
            </button>
          </div>
          <Button
            size="sm"
            onClick={onAdd}
            className="bg-teal-800 hover:bg-teal-900 text-white"
          >
            <Plus className="mr-1 h-3.5 w-3.5" />
            Crear
          </Button>
        </div>
      </CardHeader>
      {open && (
        <CardContent className="pt-0 pb-3 px-4">
          {/* Search */}
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={`Buscar ${title.toLowerCase()}...`}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>

          {/* List */}
          {isLoading ? (
            <div className="space-y-3">
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} className="h-12" />
              ))}
            </div>
          ) : (
            <div className="max-h-[350px] overflow-y-auto space-y-2">
              {filtered && filtered.length > 0 ? (
                filtered.map((item) => (
                  <div
                    key={item.id}
                    className="flex items-center justify-between p-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm truncate">{item.nombre}</span>
                        {item.activo ? (
                          <Badge className="badge-gradient hover:opacity-90 text-xs">
                            Activo
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-gray-400 border-gray-400 text-xs">
                            Inactivo
                          </Badge>
                        )}
                      </div>
                      {renderSecondary && (
                        <p className="text-xs text-muted-foreground mt-0.5 truncate">
                          {renderSecondary(item) || ''}
                        </p>
                      )}
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="ml-2 text-muted-foreground hover:text-foreground"
                        >
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => onEdit(item)}>
                          <Pencil className="mr-2 h-3.5 w-3.5" />
                          Editar
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => onDelete(item)}
                          className="text-red-600 focus:text-red-600"
                        >
                          <Trash2 className="mr-2 h-3.5 w-3.5" />
                          Eliminar
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                ))
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Icon className="h-8 w-8 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">
                    {search ? 'No se encontraron resultados' : `No hay ${title.toLowerCase()} registrados`}
                  </p>
                </div>
              )}
            </div>
          )}
        </CardContent>
      )}
    </Card>
  )
}

function DragOverlayCard({ title, icon: Icon, count }: { title: string; icon: React.ElementType; count: number }) {
  return (
    <Card className="border-2 border-teal-400 shadow-xl bg-white dark:bg-gray-900 rotate-[2deg] scale-105">
      <CardHeader className="py-3 px-4">
        <div className="flex items-center gap-2">
          <GripVertical className="h-4 w-4 text-teal-400" />
          <Icon className="h-4 w-4 text-teal-500" />
          <CardTitle className="text-base font-semibold">
            {title}
          </CardTitle>
          <span className="text-sm text-muted-foreground">
            ({count})
          </span>
        </div>
      </CardHeader>
    </Card>
  )
}

// ============================================================================
// Página principal
// ============================================================================

const STORAGE_KEY = 'bd-clientes-config-order'
const DEFAULT_ORDER = ['paises', 'cargos', 'lineas']

function getSavedOrder(): string[] {
  try {
    const saved = localStorage.getItem(STORAGE_KEY)
    if (saved) {
      const parsed = JSON.parse(saved) as string[]
      if (parsed.length === DEFAULT_ORDER.length && DEFAULT_ORDER.every(k => parsed.includes(k))) {
        return parsed
      }
    }
  } catch { /* ignore */ }
  return DEFAULT_ORDER
}

const SECTION_META: Record<string, { title: string; icon: React.ElementType }> = {
  paises: { title: 'Países', icon: Globe },
  cargos: { title: 'Cargos', icon: Briefcase },
  lineas: { title: 'Líneas', icon: Layers },
}

export function ConfiguracionBdClientesPage() {
  useDocumentTitle('BD Clientes - Configuración')

  // Fetch ALL items (no activo filter = backend returns all)
  const { data: paises, isLoading: paisesLoading } = usePaises()
  const { data: cargos, isLoading: cargosLoading } = useCargos()
  const { data: lineas, isLoading: lineasLoading } = useLineas()

  // Section order
  const [sectionOrder, setSectionOrder] = useState<string[]>(getSavedOrder)
  const [activeId, setActiveId] = useState<string | null>(null)

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(TouchSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string)
  }

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveId(null)
    const { active, over } = event
    if (over && active.id !== over.id) {
      setSectionOrder(prev => {
        const oldIndex = prev.indexOf(active.id as string)
        const newIndex = prev.indexOf(over.id as string)
        const newOrder = arrayMove(prev, oldIndex, newIndex)
        localStorage.setItem(STORAGE_KEY, JSON.stringify(newOrder))
        return newOrder
      })
    }
  }

  // Modal state
  const [paisModalOpen, setPaisModalOpen] = useState(false)
  const [cargoModalOpen, setCargoModalOpen] = useState(false)
  const [lineaModalOpen, setLineaModalOpen] = useState(false)
  const [editPais, setEditPais] = useState<PaisOption | null>(null)
  const [editCargo, setEditCargo] = useState<CargoOption | null>(null)
  const [editLinea, setEditLinea] = useState<LineaOption | null>(null)

  const eliminarPaisMutation = useEliminarPais()
  const eliminarCargoMutation = useEliminarCargo()
  const eliminarLineaMutation = useEliminarLinea()

  const handleDeletePais = (item: PaisOption) => {
    if (confirm(`¿Eliminar el país "${item.nombre}"? Si tiene clientes o laboratorios asociados, no se podrá eliminar.`)) {
      eliminarPaisMutation.mutate(item.id)
    }
  }

  const handleDeleteCargo = (item: CargoOption) => {
    if (confirm(`¿Eliminar el cargo "${item.nombre}"? Si tiene clientes asociados, no se podrá eliminar.`)) {
      eliminarCargoMutation.mutate(item.id)
    }
  }

  const handleDeleteLinea = (item: LineaOption) => {
    if (confirm(`¿Eliminar la línea "${item.nombre}"? Si tiene clientes asociados, no se podrá eliminar.`)) {
      eliminarLineaMutation.mutate(item.id)
    }
  }

  const handleOpenPaisModal = (item?: PaisOption) => {
    setEditPais(item || null)
    setPaisModalOpen(true)
  }

  const handleOpenCargoModal = (item?: CargoOption) => {
    setEditCargo(item || null)
    setCargoModalOpen(true)
  }

  const handleOpenLineaModal = (item?: LineaOption) => {
    setEditLinea(item || null)
    setLineaModalOpen(true)
  }

  const sectionProps: Record<string, { items: { id: number; nombre: string; activo: boolean }[] | undefined; isLoading: boolean; onAdd: () => void; onEdit: (item: any) => void; onDelete: (item: any) => void; renderSecondary?: (item: any) => string | null }> = {
    paises: {
      items: paises,
      isLoading: paisesLoading,
      onAdd: () => handleOpenPaisModal(),
      onEdit: (item) => handleOpenPaisModal(item),
      onDelete: (item) => handleDeletePais(item),
      renderSecondary: (item) => (item as PaisOption).codigo ? `Código: ${(item as PaisOption).codigo}` : null,
    },
    cargos: {
      items: cargos,
      isLoading: cargosLoading,
      onAdd: () => handleOpenCargoModal(),
      onEdit: (item) => handleOpenCargoModal(item as CargoOption),
      onDelete: (item) => handleDeleteCargo(item),
      renderSecondary: (item) => (item as CargoOption).descripcion,
    },
    lineas: {
      items: lineas,
      isLoading: lineasLoading,
      onAdd: () => handleOpenLineaModal(),
      onEdit: (item) => handleOpenLineaModal(item as LineaOption),
      onDelete: (item) => handleDeleteLinea(item),
      renderSecondary: (item) => (item as LineaOption).descripcion,
    },
  }

  const activeSection = activeId ? SECTION_META[activeId] : null

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
          <Settings2 className="h-6 w-6 text-black dark:text-white" />
          Configuración
        </h1>
      </div>

      {/* Info banner */}
      <div className="flex items-center gap-2 p-3 rounded-lg bg-teal-500/5 border border-teal-500/20 text-sm text-muted-foreground">
        <ShieldCheck className="h-4 w-4 text-teal-500 flex-shrink-0" />
        Los registros con clientes asociados no se pueden eliminar. Utiliza el estado "Inactivo" para ocultarlos de los formularios.
      </div>

      {/* Config sections con drag and drop */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={sectionOrder}
          strategy={verticalListSortingStrategy}
        >
          <div className="grid gap-4">
            {sectionOrder.map((key) => {
              const meta = SECTION_META[key]
              const props = sectionProps[key]
              return (
                <ConfigSection
                  key={key}
                  sectionId={key}
                  title={meta.title}
                  icon={meta.icon}
                  {...props}
                />
              )
            })}
          </div>
        </SortableContext>
        <DragOverlay dropAnimation={{
          duration: 200,
          easing: 'cubic-bezier(0.18, 0.67, 0.6, 1.22)',
        }}>
          {activeSection ? (
            <DragOverlayCard
              title={activeSection.title}
              icon={activeSection.icon}
              count={sectionProps[activeId!]?.items?.length ?? 0}
            />
          ) : null}
        </DragOverlay>
      </DndContext>

      {/* Modals */}
      {paisModalOpen && (
        <PaisModal
          open={paisModalOpen}
          onOpenChange={setPaisModalOpen}
          editItem={editPais}
        />
      )}

      {cargoModalOpen && (
        <CargoLineaModal
          open={cargoModalOpen}
          onOpenChange={setCargoModalOpen}
          editItem={editCargo}
          entityType="cargo"
        />
      )}

      {lineaModalOpen && (
        <CargoLineaModal
          open={lineaModalOpen}
          onOpenChange={setLineaModalOpen}
          editItem={editLinea}
          entityType="linea"
        />
      )}
    </div>
  )
}
